<template>
  <div class="px-4 py-3 space-y-6">
    <!-- Header -->
    <div class="flex justify-between items-center border-b border-gray-200 py-4 mb-6">
      <h1 class="text-xl font-semibold text-gray-800"> Mes Projets</h1>
      <!-- Actions -->
    </div>

    <!-- Stats Cards -->
    <div class="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
      <!-- Card: Total de Projets -->
      <div class="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
        <div class="flex items-center justify-between">
          <div class="text-sm font-medium text-gray-500">Total de Projets</div>
          <div class="p-2 rounded-xl bg-gray-50">
            <svg class="w-5 h-5 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-width="1.5" d="M12 12c2.485 0 4.5-2.015 4.5-4.5S14.485 3 12 3 7.5 5.015 7.5 7.5 9.515 12 12 12z"/>
              <path stroke-width="1.5" d="M4.5 21v-1.5C4.5 17.567 7.567 15 12 15s7.5 2.567 7.5 4.5V21"/>
            </svg>
          </div>
        </div>
        <div class="mt-3 flex items-baseline gap-2">
          <div class="text-3xl font-semibold text-gray-900">{{ totalProjets }}</div>
        </div>
      </div>

      <!-- Card: Projets finis -->
      <div class="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
        <div class="flex items-center justify-between">
          <div class="text-sm font-medium text-gray-500">Projets Finis</div>
          <div class="p-2 rounded-xl bg-gray-50">
            <svg class="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-width="1.5" d="M5 13l4 4L19 7"/>
            </svg>
          </div>
        </div>
        <div class="mt-3 flex items-baseline gap-2">
          <div class="text-3xl font-semibold text-gray-900">{{ ProjetsResolu }}</div>
        </div>
      </div>

      <!-- Card: Projets en cours -->
      <div class="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
        <div class="flex items-center justify-between">
          <div class="text-sm font-medium text-gray-500">Projets en cours</div>
          <div class="p-2 rounded-xl bg-gray-50">
            <svg class="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-width="1.5" d="M8 9h8M8 13h6M4 19l4-4h12V7H4v12z"/>
            </svg>
          </div>
        </div>
        <div class="mt-3 flex items-baseline gap-2">
          <div class="text-3xl font-semibold text-gray-900">{{ ProjetsAttente }}</div>
        </div>
      </div>

      <!-- Card: Nouveaux projets -->
      <div class="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
        <div class="flex items-center justify-between">
          <div class="text-sm font-medium text-gray-500">Nouveaux Projets</div>
          <div class="p-2 rounded-xl bg-gray-50">
            <svg class="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-width="1.5" d="M12 4v16m8-8H4"/>
            </svg>
          </div>
        </div>
        <div class="mt-3 flex items-baseline gap-2">
          <div class="text-3xl font-semibold text-gray-900">{{ prospectsAcceptees }}</div>
        </div>
      </div>
    </div>

<!-- Tableau des projets -->
<div class="overflow-x-auto bg-white shadow-sm rounded-2xl border border-gray-100">
  <table class="min-w-full divide-y divide-gray-200">
    <thead class="bg-gray-50">
      <tr>
        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Projet</th>
        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Réalisation</th>
        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timeline</th>
        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
      </tr>
    </thead>
    <tbody class="bg-white divide-y divide-gray-200">
      <tr v-for="projet in projets" :key="projet.id">
        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ projet.categorie }}</td>
        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ projet.nom }}</td>
        <td class="px-6 py-4 whitespace-nowrap text-sm">
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div
              class="h-2 rounded-full bg-blue-500"
              :style="{ width: projet.progress + '%' }"
            ></div>
          </div>
          <span class="text-xs text-gray-500">{{ projet.progress }}%</span>
        </td>
        <td class="px-6 py-4 whitespace-nowrap text-sm">
          <span
            class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
            :class="statusClass(projet.status)"
          >
            {{ projet.status }}
          </span>
        </td>
        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {{ projet.timeline }}
        </td>
        <td class="px-6 py-4 whitespace-nowrap text-sm flex gap-2">
          <button
            @click="voirProjet(projet)"
            class="p-2 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition"
            title="Voir"
          >
            <!-- Eye Icon -->
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none"
                 viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
              <path stroke-linecap="round" stroke-linejoin="round"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z"/>
            </svg>
          </button>

        </td>
      </tr>
    </tbody>
  </table>
</div>

    <!-- Pagination -->
    <div class="flex gap-6 justify-end items-center mt-4">
      <button
        @click="prevPage"
        :disabled="currentPage === 1"
        class="flex items-center gap-1 px-3 py-3 border rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
      >
    <!-- Chevron gauche -->
    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
      <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
      </button>

      <button
        v-for="page in totalPages"
        :key="page"
        @click="goToPage(page)"
        :class="['', page === currentPage ? ' text-blue-600' : ' ']"
      >
        {{ page }}
      </button>

      <button
        @click="nextPage"
        :disabled="currentPage === totalPages"
       class="flex items-center gap-1 px-3 py-3 border rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <!-- Chevron droit -->
    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
      <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
    </svg>
      </button>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue"
import { useRouter } from "vue-router"
import axios from "axios"

const router = useRouter()

// 📊 Stats
const totalProjets = ref(0)
const ProjetsResolu = ref(0)
const ProjetsAttente = ref(0)
const prospectsAcceptees = ref(0)

// 📋 Liste complète de projets
const allProjets = ref([])

// Pagination
const currentPage = ref(1)
const pageSize = ref(5) // nombre de projets par page
const totalPages = computed(() => Math.ceil(allProjets.value.length / pageSize.value))

// Projets affichés sur la page actuelle
const projets = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  const end = start + pageSize.value
  return allProjets.value.slice(start, end)
})

// 🎨 Classe selon status
const statusClass = (status) => {
  switch (status) {
    case "Terminé":
      return "bg-green-100 text-green-800"
    case "En cours":
      return "bg-blue-100 text-blue-800"
    case "En attente":
      return "bg-yellow-100 text-yellow-800"
    case "En retard":
      return "bg-red-100 text-red-800"
    default:
      return "bg-gray-100 text-gray-800"
  }
}

// 🔹 Redirection vers la page détail d’un projet
const voirProjet = (projet) => {
  router.push(`/projects/${projet.id}`)
}

// Pagination methods
const prevPage = () => {
  if (currentPage.value > 1) currentPage.value--
}
const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value++
}
const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) currentPage.value = page
}

// 🟢 Fetch projets du client connecté
const fetchProjets = async () => {
  try {
    const token = localStorage.getItem("token")
    const res = await axios.get("https://datafuseweb-95a5588f8542.herokuapp.com/api/project/mes-projets", {
      headers: { Authorization: `Bearer ${token}` },
    })

    allProjets.value = res.data.projets.map(p => ({
      id: p._id,
      nbProjets: p.etapes?.length || 0,
      client: `${p.client?.prenom || ""} ${p.client?.nom || ""}`,
      categorie: p.categorie,
      nom: p.nomProjet,
      progress: p.realisation,
      status: p.statut,
      timeline: p.timeline?.debut && p.timeline?.fin
        ? `${new Date(p.timeline.debut).toLocaleDateString()} → ${new Date(p.timeline.fin).toLocaleDateString()}`
        : "Non défini",
    }))

    // stats
    totalProjets.value = allProjets.value.length
    ProjetsResolu.value = allProjets.value.filter(p => p.status === "Terminé").length
    ProjetsAttente.value = allProjets.value.filter(p => p.status === "En attente").length
    prospectsAcceptees.value = allProjets.value.filter(p => p.status === "En cours").length

  } catch (err) {
    console.error("Erreur lors du fetch des projets :", err)
  }
}

onMounted(fetchProjets)
</script>


